import axios from 'axios'
import { getAccessContext } from '../services/accessContext'
import {
  cacheResolvedRouteReference,
  cachedInternalRouteId,
  cachedRouteReferenceLabel,
  isNumericRouteId,
  isPublicRouteId,
  setRouteReferenceAccount
} from '../common/routeRecordId'
import { publicRouteReferencePolicy } from './publicRouteReferencePolicy'

let installed = false
const pendingRequests = new Map()

function referenceRequest (operation, references) {
  const key = `${operation}:${JSON.stringify(references)}`
  if (pendingRequests.has(key)) return pendingRequests.get(key)

  const request = axios
    .post(
      `${process.env.VUE_APP_API_ORIGIN}/route-references/${operation}`,
      { references },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then(response => {
      if (!Array.isArray(response.data) || response.data.length !== references.length) {
        const error = new Error('URL参照IDの応答件数が一致しません。')
        error.code = 'PUBLIC_ROUTE_REFERENCE_CONTRACT_MISMATCH'
        throw error
      }
      response.data.forEach((row, index) => {
        const expected = references[index]
        if (!row ||
            String(row.resourceType || '').toUpperCase() !== expected.resourceType ||
            !isNumericRouteId(row.internalId) ||
            !isPublicRouteId(row.publicId)) {
          const error = new Error('URL参照IDの応答内容が正しくありません。')
          error.code = 'PUBLIC_ROUTE_REFERENCE_CONTRACT_MISMATCH'
          throw error
        }
        cacheResolvedRouteReference(
          row.publicId,
          row.internalId,
          row.resourceType,
          row.displayLabel
        )
      })
      return response.data
    })
    .finally(() => pendingRequests.delete(key))

  pendingRequests.set(key, request)
  return request
}

function hasValue (value) {
  return value !== null && value !== undefined && String(value).trim() !== ''
}

function sourceValue (route, policy, reference) {
  const params = (route && route.params) || {}
  const query = (route && route.query) || {}
  if (policy.mode === 'query') {
    if (hasValue(query[reference.publicKey])) return query[reference.publicKey]
    if (hasValue(query[reference.legacyKey])) return query[reference.legacyKey]
    return params[reference.legacyKey]
  }
  if (policy.mode === 'public-path') {
    return params[reference.publicKey]
  }
  return params[reference.legacyKey]
}

function encodeInput (route, policy) {
  return policy.references.map(reference => {
    const value = sourceValue(route, policy, reference)
    if (!isNumericRouteId(value)) return null
    return {
      resourceType: reference.resourceType,
      internalId: Number(value)
    }
  })
}

function resolveInput (route, policy) {
  return policy.references.map(reference => {
    const value = sourceValue(route, policy, reference)
    if (!isPublicRouteId(value)) return null
    return {
      resourceType: reference.resourceType,
      publicId: String(value).toLowerCase()
    }
  })
}

function allValid (values) {
  return Array.isArray(values) && values.length > 0 && values.every(Boolean)
}

function setResolvedParams (params, reference, row) {
  params[reference.publicKey] = String(row.publicId).toLowerCase()
  params[reference.internalKey] = Number(row.internalId)
  if (reference.labelKey && String(row.displayLabel || '').trim()) {
    params[reference.labelKey] = String(row.displayLabel).trim()
  }
}

function buildPublicPathTarget (to, policy, rows) {
  const params = { ...(to.params || {}) }
  delete params.value
  delete params.projectId
  ;(policy.dropParams || []).forEach(key => delete params[key])

  policy.references.forEach((reference, index) => {
    delete params[reference.legacyKey]
    setResolvedParams(params, reference, rows[index])
  })

  return {
    name: policy.publicRouteName,
    params,
    query: { ...(to.query || {}) },
    hash: to.hash,
    replace: true
  }
}

function buildResolvedPublicTarget (to, policy, references) {
  const params = { ...(to.params || {}) }
  let changed = false
  references.forEach(reference => {
    const publicId = String(sourceValue(to, policy, reference)).toLowerCase()
    const internalId = cachedInternalRouteId(publicId, reference.resourceType)
    if (!isNumericRouteId(params[reference.internalKey]) ||
        Number(params[reference.internalKey]) !== internalId) {
      params[reference.internalKey] = internalId
      changed = true
    }
    if (reference.labelKey) {
      const label = cachedRouteReferenceLabel(publicId, reference.resourceType)
      if (label && params[reference.labelKey] !== label) {
        params[reference.labelKey] = label
        changed = true
      }
    }
  })

  if (!changed) return null
  return {
    name: policy.publicRouteName,
    params,
    query: { ...(to.query || {}) },
    hash: to.hash,
    replace: true
  }
}

function buildQueryTarget (to, policy, rows) {
  const query = { ...(to.query || {}) }
  const params = { ...(to.params || {}) }
  policy.references.forEach((reference, index) => {
    if (reference.legacyKey !== reference.publicKey) {
      delete query[reference.legacyKey]
    }
    query[reference.publicKey] = String(rows[index].publicId).toLowerCase()
    params[reference.internalKey] = Number(rows[index].internalId)
  })
  delete params.value
  delete params.projectId
  return {
    name: to.name,
    params,
    query,
    hash: to.hash,
    replace: true
  }
}

function errorStatus (error) {
  return error && error.response ? Number(error.response.status) : 0
}

function nonDisclosureTarget (reason) {
  return {
    name: 'AccessDenied',
    query: { reason },
    replace: true
  }
}

/**
 * 数値の旧URLをUUIDへcanonicalizeし、UUID直リンクでは内部IDを復元する。
 *
 * <p>encodeの通信障害だけは旧数値URLをそのまま通し、業務停止を避ける。403等の
 * 認可失敗はfail-closed。UUID直リンクは内部IDなしでは画面を動かせないため、
 * 通信障害をUserErrorへ送る。Backend先行配置なら通常この互換経路は使われない。</p>
 */
export default function installPublicRouteReferenceGuard (router) {
  if (installed) return
  installed = true

  router.beforeEach((to, from, next) => {
    const policy = publicRouteReferencePolicy(to)
    if (!policy) {
      next()
      return
    }

    getAccessContext()
      .then(context => {
        setRouteReferenceAccount(context.accountId)

        if (policy.mode === 'legacy-path' || policy.mode === 'query') {
          const values = policy.references.map(reference =>
            sourceValue(to, policy, reference))
          if (policy.optional && values.every(value => !hasValue(value))) {
            next()
            return null
          }
          const hasNumeric = values.every(isNumericRouteId)
          const hasPublic = values.every(isPublicRouteId)

          if (hasNumeric) {
            const references = encodeInput(to, policy)
            return referenceRequest('encode', references)
              .then(rows => {
                next(policy.mode === 'query'
                  ? buildQueryTarget(to, policy, rows)
                  : buildPublicPathTarget(to, policy, rows))
              })
              .catch(error => {
                const status = errorStatus(error)
                if (status === 401) {
                  next({ name: 'Login' })
                  return
                }
                if (status === 403 || status === 400) {
                  next(nonDisclosureTarget('PUBLIC_ROUTE_REFERENCE_FORBIDDEN'))
                  return
                }
                // Backend未配置・一時障害で既存業務を止めない。サーバ認可は従来どおり有効。
                console.warn('公開URLへの置換を一時的に見送りました。', error)
                next()
              })
          }

          if (hasPublic && policy.mode === 'query') {
            const unresolved = policy.references.filter(reference =>
              cachedInternalRouteId(
                sourceValue(to, policy, reference),
                reference.resourceType
              ) == null)
            if (!unresolved.length) {
              next()
              return null
            }
            const references = resolveInput(to, policy)
            return referenceRequest('resolve', references)
              .then(() => next())
              .catch(error => {
                const status = errorStatus(error)
                if (status === 401) next({ name: 'Login' })
                else if (status === 403 || status === 400) {
                  next(nonDisclosureTarget('PUBLIC_ROUTE_REFERENCE_FORBIDDEN'))
                } else next({ name: 'UserError', replace: true })
              })
          }

          next(nonDisclosureTarget('PUBLIC_ROUTE_REFERENCE_INVALID'))
          return null
        }

        const references = resolveInput(to, policy)
        if (!allValid(references)) {
          next(nonDisclosureTarget('PUBLIC_ROUTE_REFERENCE_INVALID'))
          return null
        }

        const unresolved = policy.references.filter(reference =>
          cachedInternalRouteId(
            sourceValue(to, policy, reference),
            reference.resourceType
          ) == null)
        const continueWithResolvedParams = () => {
          const target = buildResolvedPublicTarget(to, policy, policy.references)
          if (target) next(target)
          else next()
        }

        if (!unresolved.length) {
          continueWithResolvedParams()
          return null
        }

        return referenceRequest('resolve', references)
          .then(continueWithResolvedParams)
          .catch(error => {
            const status = errorStatus(error)
            if (status === 401) next({ name: 'Login' })
            else if (status === 403 || status === 400) {
              next(nonDisclosureTarget('PUBLIC_ROUTE_REFERENCE_FORBIDDEN'))
            } else next({ name: 'UserError', replace: true })
          })
      })
      .catch(error => {
        const status = errorStatus(error)
        if (status === 401) next({ name: 'Login' })
        else next(nonDisclosureTarget(
          (error && error.code) || 'PUBLIC_ROUTE_REFERENCE_CONTEXT_ERROR'
        ))
      })
  })
}
